import { Box, Typography } from '@mui/material';
import { DashboardBodyCell } from '../../Common/DashboardDataCell';
import {
  differenceInDays,
  formatRentalPotential,
  pricingValue,
} from '../InitialInformationStep';
import { formatDateTolocaleDateString } from '../../../utils/formatDateTimes';
import { formatPricingBasis } from '../../../helpers/formatPricingBasis';
import { containsRentalHistoryRows } from '../../../../../shared/helpers/containsRentalHistoryRows';

type PricingSheetSummaryBoxProps = {
  meanFilledCoefficientOfAllDayRows: any;
  meanFilledCoefficientOfAllMonthRows: any;
  meanFilledCoefficientOfAllDayRowsWeight: any;
  meanFilledCoefficientOfAllMonthRowsWeight: any;
  paymentTermDays: any;
  preSelectedRows: any;
  potential: any;
  priceSheetStartDate: any;
  priceSheetEndDate: any;
  pricingBasis: any;
  priceSheetType: any;
  totalProfitDay: number;
  totalProfitMonth: number;
  totalEstimatedYieldDay: number;
  totalEstimatedYieldMonth: number;
};

const asCurrency = new Intl.NumberFormat('fi-FI', {
  style: 'currency',
  currency: 'EUR',
});

export const PricingSheetSummaryBox = ({
  meanFilledCoefficientOfAllDayRows,
  meanFilledCoefficientOfAllMonthRowsWeight,
  meanFilledCoefficientOfAllMonthRows,
  meanFilledCoefficientOfAllDayRowsWeight,
  paymentTermDays,
  preSelectedRows,
  potential,
  priceSheetStartDate,
  priceSheetEndDate,
  pricingBasis,
  priceSheetType,
  totalProfitDay,
  totalProfitMonth,
  totalEstimatedYieldDay,
  totalEstimatedYieldMonth,
}: PricingSheetSummaryBoxProps) => {
  const containsRentalHistory =
    containsRentalHistoryRows(preSelectedRows);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          width: '100%',
        }}
      >
        <Box sx={{ flex: '0 0 40%', boxSizing: 'border-box' }}>
          <Typography
            sx={{ mt: 1 }}
            fontWeight={'bold'}
            variant="body1"
            component="div"
            gutterBottom
            color="text.primary"
          >
            {'Painotettu kokonaishintakerroin'}
          </Typography>
          <Typography
            sx={{ mt: 0, pt: 0 }}
            variant="body1"
            component="div"
            gutterBottom
            color="text.primary"
          >
            {`Päivä: ${!isNaN(meanFilledCoefficientOfAllDayRowsWeight) ? meanFilledCoefficientOfAllDayRowsWeight?.toFixed(2) : '-'} `}
          </Typography>
          {pricingBasis !== 'DAY' && (
            <Typography
              sx={{ mt: 0, pt: 0 }}
              variant="body1"
              component="div"
              gutterBottom
              color="text.primary"
            >
              {`Kuukausi: ${!isNaN(meanFilledCoefficientOfAllMonthRowsWeight) ? meanFilledCoefficientOfAllMonthRowsWeight?.toFixed(2) : '-'}`}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: '0 0 40%', boxSizing: 'border-box' }}>
          <Typography
            sx={{ mt: 1 }}
            fontWeight={'bold'}
            variant="body1"
            component="div"
            gutterBottom
            color="text.primary"
          >
            {'Kokonaishintakerroin'}
          </Typography>
          <Typography
            sx={{ mt: 0, pt: 0 }}
            variant="body1"
            component="div"
            gutterBottom
            color="text.primary"
          >
            {`Päivä: ${meanFilledCoefficientOfAllDayRows?.toFixed(2)}`}
          </Typography>
          {pricingBasis !== 'DAY' && (
            <Typography
              sx={{ mt: 0, pt: 0 }}
              variant="body1"
              component="div"
              gutterBottom
              color="text.primary"
            >
              {`Kuukausi: ${meanFilledCoefficientOfAllMonthRows?.toFixed(2)}`}
            </Typography>
          )}
        </Box>

        <Box sx={{ flex: '0 0 40%', boxSizing: 'border-box' }}>
          <Typography
            sx={{ mt: 1 }}
            fontWeight={'bold'}
            variant="body1"
            component="div"
            gutterBottom
            color="text.primary"
          >
            {'Viimeisen vuoden tuotto'}
          </Typography>
          <Typography
            sx={{ mt: 0, pt: 0 }}
            variant="body1"
            component="div"
            gutterBottom
            color="text.primary"
          >
            {`Päivä: ${containsRentalHistory && totalProfitDay ? `${asCurrency.format(totalProfitDay)}` : '-'} `}
          </Typography>
          {pricingBasis !== 'DAY' && (
            <Typography
              sx={{ mt: 0, pt: 0 }}
              variant="body1"
              component="div"
              gutterBottom
              color="text.primary"
            >
              {`Kuukausi: ${containsRentalHistory && totalProfitMonth ? `${asCurrency.format(totalProfitMonth)}` : '-'} `}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: '0 0 40%', boxSizing: 'border-box' }}>
          <Typography
            sx={{ mt: 1 }}
            fontWeight={'bold'}
            variant="body1"
            component="div"
            gutterBottom
            color="text.primary"
          >
            {'Laskennallinen tuotto uudella hinnastolla'}
          </Typography>
          <Typography
            sx={{ mt: 0, pt: 0 }}
            variant="body1"
            component="div"
            gutterBottom
            color="text.primary"
          >
            {`Päivä: ${containsRentalHistory && totalEstimatedYieldDay ? `${asCurrency.format(totalEstimatedYieldDay)}` : '-'}`}
          </Typography>
          {pricingBasis !== 'DAY' && (
            <Typography
              sx={{ mt: 0, pt: 0 }}
              variant="body1"
              component="div"
              gutterBottom
              color="text.primary"
            >
              {`Kuukausi: ${containsRentalHistory ? `${asCurrency.format(totalEstimatedYieldMonth)}` : '-'}`}
            </Typography>
          )}
        </Box>

        <DashboardBodyCell
          styleProps={{
            flex: '0 0 40%',
            boxSizing: 'border-box',
          }}
          header={'Maksuaika'}
          body={paymentTermDays + ' päivää'}
        />
        <DashboardBodyCell
          styleProps={{
            flex: '0 0 40%',
            boxSizing: 'border-box',
          }}
          header={'Tuotteiden määrä'}
          body={preSelectedRows.length ?? 0}
        />
        <DashboardBodyCell
          styleProps={{
            flex: '0 0 40%',
            boxSizing: 'border-box',
          }}
          header={'Sopimuksen arvo'}
          body={pricingValue(
            potential,
            differenceInDays(priceSheetStartDate, priceSheetEndDate),
          )}
        />
        <DashboardBodyCell
          styleProps={{
            flex: '0 0 40%',
            boxSizing: 'border-box',
          }}
          header={'Vuokrauspotentiaali'}
          body={formatRentalPotential(
            potential?.bottom,
            potential?.top,
          )}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
        }}
      >
        <DashboardBodyCell
          header={'Voimassa'}
          body={`${
            priceSheetStartDate
              ? formatDateTolocaleDateString(priceSheetStartDate)
              : 'Ei aloituspäivämäärää'
          } - ${
            priceSheetEndDate
              ? formatDateTolocaleDateString(priceSheetEndDate)
              : 'Ei lopetuspäivämäärää'
          }`}
        />
        <DashboardBodyCell
          header={'Tyyppi'}
          body={formatPricingBasis(pricingBasis)}
        />
        <DashboardBodyCell header={'Taso'} body={priceSheetType} />
      </Box>
    </Box>
  );
};
